import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { PaginatedResponse } from '@/types/api.types';

// API response wrapper type
interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

// API EPC response type
interface ApiEpc {
  epcTag: string;
  status: string;
  assetId: string | null;
  assetCode: string | null;
  assetName: string | null;
  entity: string;
}

export interface Epc {
  id: string;
  code: string;
  assetId: string | null;
  assetCode: string | null;
  assetName: string | null;
  status: string;
  entity: string;
}

export interface EpcSearchParams {
  search?: string;
  status?: string;
  page?: number;
  pageSize?: number;
}

// Query keys
export const epcKeys = {
  all: ['epcs'] as const,
  lists: () => [...epcKeys.all, 'list'] as const,
  list: (params: EpcSearchParams) => [...epcKeys.lists(), params] as const,
  details: () => [...epcKeys.all, 'detail'] as const,
  detail: (id: string) => [...epcKeys.details(), id] as const,
  available: () => [...epcKeys.all, 'available'] as const,
};

// Transform API EPC to our Epc interface
function transformEpc(apiEpc: ApiEpc): Epc {
  return {
    id: apiEpc.epcTag,
    code: apiEpc.epcTag,
    assetId: apiEpc.assetId,
    assetCode: apiEpc.assetCode,
    assetName: apiEpc.assetName,
    status: apiEpc.status,
    entity: apiEpc.entity,
  };
}

// Search EPCs
export function useEpcs(params: EpcSearchParams) {
  return useQuery({
    queryKey: epcKeys.list(params),
    queryFn: async () => {
      const response = await api.get<ApiResponse<{ data: ApiEpc[]; totalRecords: number; assignedCount: number; availableCount: number; inactiveCount: number }>>('/api/epc/list', {
        params: {
          searchStr: params.search,
          status: params.status,
          page: params.page || 1,
          pageSize: params.pageSize || 25,
        },
      });
      const apiData = response.data.data;
      return {
        items: apiData.data.map(transformEpc),
        totalCount: apiData.totalRecords,
        assignedCount: apiData.assignedCount,
        availableCount: apiData.availableCount,
        inactiveCount: apiData.inactiveCount,
        totalPages: Math.ceil(apiData.totalRecords / (params.pageSize || 25)),
        page: params.page || 1,
        pageSize: params.pageSize || 25,
      } as PaginatedResponse<Epc> & { assignedCount: number; availableCount: number; inactiveCount: number };
    },
  });
}

// Get available EPCs (for assignment) - filter from list
export function useAvailableEpcs() {
  return useQuery({
    queryKey: epcKeys.available(),
    queryFn: async () => {
      const response = await api.get<ApiResponse<{ data: ApiEpc[]; totalRecords: number }>>('/api/epc/list', {
        params: { pageSize: 1000 },
      });
      return response.data.data.data
        .filter(epc => epc.status === 'Disponible')
        .map(transformEpc);
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
}

// Get single EPC
export function useEpc(epcTag: string, enabled = true) {
  return useQuery({
    queryKey: epcKeys.detail(epcTag),
    queryFn: async () => {
      const response = await api.get<ApiResponse<ApiEpc>>('/api/epc/get', {
        params: { epcTag },
      });
      return transformEpc(response.data.data);
    },
    enabled: enabled && epcTag.length > 0,
  });
}

// Create EPC
export function useCreateEpc() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { code: string }) => {
      const response = await api.post<ApiResponse<string>>('/api/epc/save', {
        epcTag: data.code,
        status: 'Disponible',
      });
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: epcKeys.lists() });
      queryClient.invalidateQueries({ queryKey: epcKeys.available() });
    },
  });
}

// Assign EPC to asset (link)
export function useAssignEpc() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ epcTag, assetId }: { epcTag: string; assetId: string }) => {
      const response = await api.post<ApiResponse<unknown>>('/api/epc/link', {
        epcTag,
        assetId,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: epcKeys.lists() });
      queryClient.invalidateQueries({ queryKey: epcKeys.available() });
    },
  });
}

// Unassign EPC from asset (unlink)
export function useUnassignEpc() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (epcTag: string) => {
      const response = await api.post<ApiResponse<unknown>>('/api/epc/unlink', {
        epcTag,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: epcKeys.lists() });
      queryClient.invalidateQueries({ queryKey: epcKeys.available() });
    },
  });
}

// Bulk upload EPCs
export function useBulkUploadEpcs() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post<ApiResponse<{ importedCount: number; duplicateCount: number }>>('/api/epc/upload/bulk', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: epcKeys.lists() });
      queryClient.invalidateQueries({ queryKey: epcKeys.available() });
    },
  });
}

// Export EPCs
export function useExportEpcs() {
  return useMutation({
    mutationFn: async (params: { search?: string }) => {
      const response = await api.post('/api/epc/export', {
        searchStr: params.search,
      }, {
        responseType: 'blob',
      });
      return response.data;
    },
  });
}

// Delete EPC - Note: API doesn't have a delete endpoint
export function useDeleteEpc() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (_epcTag: string) => {
      // API doesn't have a direct delete endpoint
      console.warn('Delete EPC not implemented in API');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: epcKeys.lists() });
      queryClient.invalidateQueries({ queryKey: epcKeys.available() });
    },
  });
}
